import React, { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

interface InvisibleRecaptchaProps {
  onVerify?: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  onLoad?: () => void;
}

export interface InvisibleRecaptchaHandle {
  execute: () => Promise<string | null>;
  reset: () => void;
  isReady: () => boolean;
}

export const InvisibleRecaptcha = forwardRef<InvisibleRecaptchaHandle, InvisibleRecaptchaProps>(
  ({ onVerify, onExpired, onError, onLoad }, ref) => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useImperativeHandle(ref, () => ({
      execute: async () => {
        if (!isLoaded) {
          console.error('reCAPTCHA not loaded yet');
          onError?.();
          return null;
        }

        if (recaptchaRef.current) {
          try {
            // Reset before executing to get a fresh token
            recaptchaRef.current.reset();
            const token = await recaptchaRef.current.executeAsync();
            if (token) {
              onVerify?.(token);
            }
            return token;
          } catch (error) {
            console.error('reCAPTCHA execution error:', error);
            onError?.();
            return null;
          }
        }
        return null;
      },
      reset: () => {
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      },
      isReady: () => isLoaded,
    }));

    const handleLoad = () => {
      console.log('reCAPTCHA script loaded successfully');
      setIsLoaded(true);
      onLoad?.();
    };

    if (!process.env.RECAPTCHA_SITE_KEY) {
      console.error('RECAPTCHA_SITE_KEY is not set in environment variables');
      return null;
    }

    return (
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.RECAPTCHA_SITE_KEY}
        size="invisible"
        asyncScriptOnLoad={handleLoad}
        onChange={(token) => {
          if (token) {
            console.log('reCAPTCHA token received');
            onVerify?.(token);
          }
        }}
        onExpired={() => {
          console.log('reCAPTCHA expired');
          onExpired?.();
        }}
        onErrored={() => {
          console.error('reCAPTCHA error');
          onError?.();
        }}
      />
    );
  },
);

InvisibleRecaptcha.displayName = 'InvisibleRecaptcha';
